import { useContainerSize } from '@bimser/core';
import IconBreadCrumbArrow from '@bimser/icons/16/breadcrumps-arrow';
import { Dropdown } from 'antd';
import * as React from 'react';
import BCButton from '../../BCButton';
import Styles from '../assets/style.scss';
import { IPaginationItem, IPaginationProps, IPaginationState, ISize } from '../entities';
import PaginationItem from './PaginationItem';

const maxItemWidth = 136;

function useMergeState(
	initialState: IPaginationState
): [IPaginationState, (state: IPaginationState) => void] {
	const [state, setState] = React.useState<IPaginationState>(initialState);
	const setMergedState = newState =>
		setState(prevState => Object.assign({}, prevState, newState));
	return [state, setMergedState];
}

function Pagination(props: IPaginationProps) {
	const targetRef = React.useRef<HTMLUListElement>();
	const dimensions = useContainerSize(targetRef);

	let breadcrumbItems: IPaginationItem[] = [];
	let leftDropdownItems: IPaginationItem[] = [];
	let rightDropdownItems: IPaginationItem[] = [];

	const [state, setState] = useMergeState({
		itemCount: 0,
		itemWidthsList: [],
		itemKeys: []
	});

	const calculateItemMembers = () => {
		if (state.itemCount > 0) {
			const activeItemPosition = getActiveItemPosition();
			const sideShowCount = Math.floor(state.itemCount / 2);
			const leftSlice = props.items.slice(0, activeItemPosition);
			const activeItem = props.items[activeItemPosition];
			const rightSlice = props.items.slice(activeItemPosition + 1, props.items.length);

			let missingLeftItemCount = 0;
			let missingRightItemCount = 0;

			if (leftSlice.length < sideShowCount) {
				missingLeftItemCount = sideShowCount - leftSlice.length;
			} else if (rightSlice.length < sideShowCount) {
				missingRightItemCount =
					sideShowCount - rightSlice.length - (state.itemCount % 2 ? 0 : 1);
			}

			const leftBreadcrumbItems = leftSlice.slice(
				Math.max(0, leftSlice.length - sideShowCount - missingRightItemCount),
				leftSlice.length
			);
			const rightBreadcrumbItems = rightSlice.slice(
				0,
				Math.max(0, sideShowCount + missingLeftItemCount - (state.itemCount % 2 ? 0 : 1))
			);

			breadcrumbItems = [...leftBreadcrumbItems, activeItem, ...rightBreadcrumbItems];
			leftDropdownItems = leftSlice.slice(
				0,
				Math.max(0, leftSlice.length - sideShowCount - missingRightItemCount)
			);
			rightDropdownItems = rightSlice.slice(
				Math.max(
					0,
					rightSlice.length -
						(rightSlice.length - sideShowCount) +
						missingLeftItemCount -
						(state.itemCount % 2 ? 0 : 1)
				),
				rightSlice.length
			);
		} else {
			breadcrumbItems = props.items;
			leftDropdownItems = [];
			rightDropdownItems = [];
		}

		calculateItemCount();
	};

	const getActiveItemPosition = () => {
		if (props.items.length == 1) {
			return 0;
		}
		return props.items.findIndex(item => item.isActive);
	};

	const renderBreadcrumbItems = () =>
		breadcrumbItems.map((item: IPaginationItem) => {
			if (item) {
				return (
					<PaginationItem
						key={item.id}
						data={item}
						onSize={(size: ISize) => getItemWidth(size, item)}
					/>
				);
			}
		});

	const calculateItemCount = () => {
		let totalWidth = 0;
		const bothOverflowButtonVisible = rightDropdownItems?.length ? true : false;
		const containerWidth = dimensions.width;
		let num = 0;
		let count = 0;

		const items = state.itemWidthsList;
		for (const item in items) {
			if (totalWidth + maxItemWidth > containerWidth) {
				if (bothOverflowButtonVisible) {
					if (num == 2) {
						count = 1;
					} else {
						count = num - 2;
					}
				} else {
					count = num - 1;
				}
				break;
			} else {
				totalWidth += items[item];
				num++;
			}
		}

		if (state.itemCount !== count) {
			setState({ itemCount: count });
		}
	};

	const getItemWidth = (size: ISize, item: IPaginationItem) => {
		const { width } = size;
		const minWidth = 20;

		const itemIndex = state.itemKeys.findIndex(i => i === item.id);
		if (width === state.itemWidthsList[itemIndex]) {
			return;
		}

		if (width < maxItemWidth && width > minWidth) {
			if (itemIndex < 0) {
				setState({
					itemWidthsList: [...state.itemWidthsList, width],
					itemKeys: [...state.itemKeys, item.id]
				});
			} else {
				if (width !== state.itemWidthsList[itemIndex]) {
					setState({
						itemWidthsList: Object.assign([], state.itemWidthsList, {
							[itemIndex]: width
						})
					});
				}
			}
		}
	};

	const renderOverflowItem = (item: IPaginationItem) => ({
		key: item.id,
		label: item.title
	});

	const handleMenuClick = React.useCallback(
		(e: any) => {
			const clickedItem = props.items.find((item: IPaginationItem) => item.id === e.key);
			clickedItem.onClick(e.domEvent, clickedItem);
		},
		[props.items]
	);

	const renderOverflowItems = (position: string) => {
		let overflowItems: IPaginationItem[] = [];

		if (position === 'start') {
			overflowItems = leftDropdownItems;
		} else {
			overflowItems = rightDropdownItems;
		}

		if (!overflowItems.length) {
			return;
		}

		return (
			<li className={[Styles.paginationItem, 'paginationItem'].join(' ')}>
				<Dropdown
					menu={{
						onClick: handleMenuClick,
						items: overflowItems.map((item: IPaginationItem) =>
							renderOverflowItem(item)
						)
					}}
					placement="bottomLeft"
				>
					<BCButton cssClass={[Styles.overflowBtn, 'overflowBtn'].join(' ')}>
						{'...'}
					</BCButton>
				</Dropdown>
				<i className={Styles.arrow}>
					<IconBreadCrumbArrow />
				</i>
			</li>
		);
	};

	calculateItemMembers();

	return (
		<ul
			className={Styles.paginationContainer}
			ref={targetRef}
		>
			{renderOverflowItems('start')}
			{renderBreadcrumbItems()}
			{renderOverflowItems('end')}
		</ul>
	);
}

export default Pagination;
